import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useMarkMessagesRead(
  conversationId: string | null,
  enabled: boolean = true
) {
  useEffect(() => {
    if (!conversationId || !enabled) return;

    const markAsRead = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('useMarkMessagesRead: No user found');
          return;
        }

        console.log('useMarkMessagesRead: Starting for conversation:', conversationId);

        // First, check if the user is the homeowner of this conversation
        // If they are, treat them as a homeowner regardless of system manager status
        const { data: conversation, error: convError } = await supabase
          .from('conversations')
          .select('homeowner_id')
          .eq('id', conversationId)
          .single();

        if (convError) {
          console.error('useMarkMessagesRead: Error fetching conversation:', convError);
          return;
        }

        console.log('useMarkMessagesRead: Conversation homeowner_id:', conversation?.homeowner_id, 'User id:', user.id);

        let userType: 'homeowner' | 'manager';
        
        if (conversation?.homeowner_id === user.id) {
          // User is the homeowner of this conversation, treat as homeowner
          userType = 'homeowner';
          console.log('useMarkMessagesRead: Treating user as homeowner');
        } else {
          // User is not the homeowner, check if they're a system manager
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_system_manager')
            .eq('id', user.id)
            .single();

          const isManager = profile?.is_system_manager || false;
          userType = isManager ? 'manager' : 'homeowner';
          console.log('useMarkMessagesRead: User is not homeowner, isManager:', isManager, 'userType:', userType);
        }

        // Try direct update approach (like ConversationsTab does) for better reliability
        const now = new Date().toISOString();

        if (userType === 'homeowner') {
          // Mark messages as read by homeowner
          const { error: updateError, data: updatedMessages } = await supabase
            .from('conversation_messages')
            .update({ read_by_homeowner_at: now })
            .eq('conversation_id', conversationId)
            .is('read_by_homeowner_at', null)
            .in('sender_type', ['manager', 'ai'])
            .select('id');

          if (updateError) {
            console.error('useMarkMessagesRead: Error updating messages:', updateError);
            // Fallback to RPC function
            const { error: rpcError } = await supabase.rpc('mark_messages_read', {
              p_conversation_id: conversationId,
              p_user_type: userType
            });
            if (rpcError) {
              console.error('useMarkMessagesRead: RPC fallback also failed:', rpcError);
            }
            return;
          }

          console.log('useMarkMessagesRead: Updated', updatedMessages?.length || 0, 'messages as read');

          // Reset homeowner unread count
          const { error: convUpdateError } = await supabase
            .from('conversations')
            .update({ homeowner_unread_count: 0 })
            .eq('id', conversationId);

          if (convUpdateError) {
            console.error('useMarkMessagesRead: Error updating conversation:', convUpdateError);
          }

          // Cancel pending notifications
          if (updatedMessages && updatedMessages.length > 0) {
            const messageIds = updatedMessages.map(m => m.id);
            await supabase
              .from('message_notifications')
              .update({ status: 'cancelled' })
              .in('message_id', messageIds)
              .eq('status', 'pending');
          }
        } else {
          // For managers, use RPC function
          const { error: rpcError } = await supabase.rpc('mark_messages_read', {
            p_conversation_id: conversationId,
            p_user_type: userType
          });

          if (rpcError) {
            console.error('useMarkMessagesRead: Error with RPC:', rpcError);
          }
        }

        console.log('useMarkMessagesRead: Successfully marked messages as read');
      } catch (err) {
        console.error('useMarkMessagesRead: Unexpected error:', err);
      }
    };

    // Mark as read when conversation is opened
    markAsRead();

    // Also mark as read when window/tab regains focus (web only)
    if (typeof window !== 'undefined') {
      const handleFocus = () => {
        markAsRead();
      };

      window.addEventListener('focus', handleFocus);

      return () => {
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [conversationId, enabled]);
}
